import { BlogArticle } from '@/lib/blog-provider';

interface BlogSchemaProps {
  article: BlogArticle;
  url: string;
}

export function BlogSchema({ article, url }: BlogSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl,
    "author": {
      "@type": "Person",
      "name": article.author,
      "description": article.authorBio
    },
    "publisher": {
      "@type": "Organization",
      "name": "AI Attractiveness Test",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.aiattractiveness.com/favicon.ico"
      }
    },
    "datePublished": article.publishDate,
    "dateModified": article.updateDate || article.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "keywords": article.tags?.join(', '),
    "articleSection": article.category,
    "wordCount": Math.ceil(article.content.length / 5),
    "timeRequired": `PT${article.readTime}M`,
    "genre": "Beauty, AI, Technology",
    "about": {
      "@type": "Thing",
      "name": "Beauty Analysis",
      "description": "AI-powered beauty and attractiveness analysis"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface BlogListSchemaProps {
  articles: BlogArticle[];
  url: string;
}

export function BlogListSchema({ articles, url }: BlogListSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "AI Attractiveness Test Blog",
    "description": "Explore the latest insights about beauty standards, AI analysis, photography tips, and the science behind attractiveness",
    "url": url,
    "publisher": {
      "@type": "Organization",
      "name": "AI Attractiveness Test",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.aiattractiveness.com/favicon.ico"
      }
    },
    "blogPost": articles.map(article => ({
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.imageUrl,
      "author": {
        "@type": "Person",
        "name": article.author
      },
      "datePublished": article.publishDate,
      "url": `https://www.aiattractiveness.com/blog/${article.slug}`
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface WebsiteSchemaProps {
  url: string;
}

export function WebsiteSchema({ url }: WebsiteSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AI Attractiveness Test",
    "description": "Free AI-powered beauty and attractiveness test. Upload your photo and get an instant attractiveness score with detailed analysis and personalized recommendations.",
    "url": url,
    "publisher": {
      "@type": "Organization",
      "name": "AI Attractiveness Test",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.aiattractiveness.com/favicon.ico"
      }
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.aiattractiveness.com/blog?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://www.instagram.com/aiattractiveness",
      "https://www.twitter.com/aiattractiveness"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface FAQSchemaProps {
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface WebApplicationSchemaProps {
  name: string;
  description: string;
  url: string;
}

export function WebApplicationSchema({ name, description, url }: WebApplicationSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": name,
    "description": description,
    "url": url,
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web Browser",
    "provider": {
      "@type": "Organization",
      "name": "AI Attractiveness Test",
      "url": "https://www.aiattractiveness.com"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5",
      "worstRating": "1"
    },
    "screenshot": `${url}/og-image.jpg`,
    "featureList": [
      "AI-powered facial analysis",
      "Beauty score calculation",
      "Facial symmetry assessment",
      "Golden ratio analysis",
      "Instant results",
      "Privacy protection"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface ServiceSchemaProps {
  name: string;
  description: string;
  url: string;
}

export function ServiceSchema({ name, description, url }: ServiceSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": url,
    "provider": {
      "@type": "Organization",
      "name": "AI Attractiveness Test",
      "url": "https://www.aiattractiveness.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.aiattractiveness.com/favicon.ico"
      }
    },
    "serviceType": "Beauty Analysis Service",
    "category": "AI Beauty Assessment",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Attractiveness Tests",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Attractiveness Test"
          },
          "price": "0",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Face Attractiveness Test"
          },
          "price": "0",
          "priceCurrency": "USD"
        }
      ]
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: Array<{
    name: string;
    text: string;
    image?: string;
  }>;
}

export function HowToSchema({ name, description, steps }: HowToSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "image": "https://www.aiattractiveness.com/guides/how-to-use.jpg",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "0"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Digital photo of face"
      },
      {
        "@type": "HowToSupply", 
        "name": "Web browser or mobile device"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "AI Attractiveness Test Platform"
      }
    ],
    "totalTime": "PT2M",
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "name": step.name,
      "text": step.text,
      "position": index + 1,
      "image": step.image || "https://www.aiattractiveness.com/guides/step-default.jpg"
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}